/**
 * useLoadingState - Loading and data fetch state management
 *
 * Manages all loading-related states including:
 * - General loading state
 * - Polygon fetch status
 * - Test file toggle
 * - Project restoration tracking
 * - Loading mode (new/existing/manual)
 * - Simplification parameters
 */

import { useState, useCallback } from 'react';

export type PolygonStatus = 'pending' | 'fetching' | 'complete' | 'error';
export type LoadingMode = 'new' | 'existing' | 'manual';

export interface LoadingState {
  // Loading flags
  loading: boolean;
  polygonStatus: PolygonStatus;

  // Configuration
  useTestFiles: boolean;
  bypassPolygonFetch: boolean;
  loadingMode: LoadingMode;

  // Restoration tracking
  restorationComplete: boolean;
  hasInitialFit: boolean;

  // Simplification parameters
  tolerance: number;
  angleThreshold: number;
}

export interface LoadingStateActions {
  // Loading setters
  setLoading: (loading: boolean) => void;
  setPolygonStatus: (status: PolygonStatus) => void;

  // Configuration setters
  setUseTestFiles: (use: boolean) => void;
  setBypassPolygonFetch: (bypass: boolean) => void;
  setLoadingMode: (mode: LoadingMode) => void;

  // Restoration setters
  setRestorationComplete: (complete: boolean) => void;
  setHasInitialFit: (fit: boolean) => void;

  // Simplification setters
  setTolerance: (tolerance: number) => void;
  setAngleThreshold: (threshold: number) => void;

  // Convenience methods
  startLoading: () => void;
  stopLoading: () => void;
  resetLoadingState: () => void;
}

export interface UseLoadingStateOptions {
  initialBypassPolygonFetch?: boolean;
  initialLoadingMode?: LoadingMode;
}

export function useLoadingState(options: UseLoadingStateOptions = {}): LoadingState & LoadingStateActions {
  const {
    initialBypassPolygonFetch = false,
    initialLoadingMode = 'new',
  } = options;

  // Loading flags
  const [loading, setLoading] = useState(true);
  const [polygonStatus, setPolygonStatus] = useState<PolygonStatus>('pending');

  // Configuration
  const [useTestFiles, setUseTestFiles] = useState(true);
  const [bypassPolygonFetch, setBypassPolygonFetch] = useState(initialBypassPolygonFetch);
  const [loadingMode, setLoadingMode] = useState<LoadingMode>(initialLoadingMode);

  // Restoration tracking
  const [restorationComplete, setRestorationComplete] = useState(false);
  const [hasInitialFit, setHasInitialFit] = useState(false);

  // Simplification parameters
  const [tolerance, setTolerance] = useState(0.5);
  const [angleThreshold, setAngleThreshold] = useState(10.0);

  // Convenience methods
  const startLoading = useCallback(() => {
    setLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    setLoading(false);
  }, []);

  const resetLoadingState = useCallback(() => {
    setLoading(true);
    setPolygonStatus('pending');
    setRestorationComplete(false);
    setHasInitialFit(false);
  }, []);

  return {
    // State
    loading,
    polygonStatus,
    useTestFiles,
    bypassPolygonFetch,
    loadingMode,
    restorationComplete,
    hasInitialFit,
    tolerance,
    angleThreshold,

    // Setters
    setLoading,
    setPolygonStatus,
    setUseTestFiles,
    setBypassPolygonFetch,
    setLoadingMode,
    setRestorationComplete,
    setHasInitialFit,
    setTolerance,
    setAngleThreshold,

    // Convenience methods
    startLoading,
    stopLoading,
    resetLoadingState,
  };
}

export default useLoadingState;
